import React from 'react';
import { gql, useQuery } from '@apollo/client';
import Card from '../../../../ui/canon/Card';
import Button from '../../../../ui/canon/Button';

const GET_CURRENT_USER = gql`
  query GetCurrentUserForMiniSprint {
    getCurrentUser { id }
  }
`;

const GET_ACTIVE_SPRINT = gql`
  query GetActiveSprint($ownerId: ID, $status: [SprintStatusEnum!]) {
    sprints(owner_user_id: $ownerId, status: $status) {
      id
      name
      startOn
      endOn
      status
      items { id title status dueAt domain cepaCategory }
    }
  }
`;

const calcStatus = (items, endOn) => {
  const total = items.length || 0;
  const done = items.filter((i) => String(i.status).toLowerCase() === 'completed').length;
  const pct = total ? (done / total) : 0;
  const today = new Date();
  const deadline = endOn ? new Date(endOn) : null;
  const overdue = items.some((i) => i.dueAt && new Date(i.dueAt) < today && String(i.status).toLowerCase() !== 'completed');
  if (overdue) return 'At Risk';
  if (deadline && today > deadline && pct < 1) return 'Behind';
  return 'On Track';
};

const ProgressBar = ({ value }) => (
  <div className="w-full h-2 bg-succession-hover-bg rounded">
    <div className="h-2 bg-succession-primary rounded transition-all duration-300" style={{ width: `${Math.round(value * 100)}%` }} />
  </div>
);

const StatusPill = ({ status }) => {
  const color = status === 'On Track' ? 'text-green-700 bg-green-50 border-green-200' : status === 'At Risk' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-red-700 bg-red-50 border-red-200';
  const Icon = () => {
    if (status === 'On Track') {
      return (
        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 17l6-6 4 4 7-7" /></svg>
      );
    }
    if (status === 'At Risk') {
      return (
        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
      );
    }
    return (
      <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
    );
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs ${color}`}>
      <Icon />{status}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  if (!type) return null;
  const t = String(type).toLowerCase();
  const style = t === 'identify' ? 'bg-blue-50 text-blue-700 border-blue-200' : t === 'build' ? 'bg-purple-50 text-purple-700 border-purple-200' : t === 'audit' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-succession-hover-bg text-succession-text-secondary border-succession-border';
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] border ${style}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
  );
};

const DomainBadge = ({ domain }) => {
  const d = String(domain || '').toLowerCase();
  const Icon = () => d === 'personal' ? (
    <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-3-3.87M4 21v-2a4 4 0 013-3.87M7 7a4 4 0 108 0 4 4 0 10-8 0z"/></svg>
  ) : (
    <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21V7a2 2 0 012-2h3v16"/><path d="M9 21V5h3v16"/><path d="M15 21V10h3a2 2 0 012 2v9"/></svg>
  );
  const label = d ? (d.charAt(0).toUpperCase() + d.slice(1)) : '';
  return (
    <span className="inline-flex items-center text-[9px] text-succession-text-secondary"><Icon />{label}</span>
  );
};

const MiniSprintDashboard = () => {
  const { data: cu } = useQuery(GET_CURRENT_USER, { fetchPolicy: 'cache-first' });
  const ownerId = cu?.getCurrentUser?.id;
  const { data, loading, error } = useQuery(GET_ACTIVE_SPRINT, { variables: { ownerId, status: ['ACTIVE'] }, skip: !ownerId, fetchPolicy: 'cache-first' });

  if (loading) {
    return (
      <div className="mt-1 flex-1 flex flex-col">
        <div className="mt-4 space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-16 rounded bg-succession-bg-secondary animate-pulse border border-succession-border" />
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-1 flex-1 flex flex-col">
        <div className="mt-4 text-succession-error text-sm">Unable to load sprint.</div>
      </div>
    );
  }

  const sprints = data?.sprints || [];
  const sprint = sprints.find((s) => String(s.status).toUpperCase() === 'ACTIVE');

  if (!sprint) {
    return (
      <div className="px-4 py-5 sm:p-6 h-full flex items-center justify-center text-center">
        <div>
          <h3 className="mb-1">Sprint</h3>
          <p className="text-sm text-succession-text-secondary">Kickstart your Master Action Plan with a Sprint. Sprints break your goals into focused 90-day actions.</p>
          <div className="mt-3">
            <Button as="a" href="/succession/sprint">Create Sprint</Button>
          </div>
        </div>
      </div>
    );
  }

  const items = sprint.items || [];
  const total = items.length;
  const done = items.filter((i) => String(i.status).toLowerCase() === 'completed').length;
  const pct = total ? done / total : 0;
  const statusText = calcStatus(items, sprint.endOn);

  // Task snapshot: up to 3, preferring open/to do then in_progress, then completed
  const open = items.filter((i) => String(i.status).toLowerCase() === 'open');
  const inProgress = items.filter((i) => String(i.status).toLowerCase() === 'in_progress');
  const completed = items.filter((i) => String(i.status).toLowerCase() === 'completed');
  const snapshot = [...open, ...inProgress, ...completed].slice(0, 3);

  // Optional insights: overdue count and breakdown
  const today = new Date();
  const overdueCount = items.filter((i) => i.dueAt && new Date(i.dueAt) < today && String(i.status).toLowerCase() !== 'completed').length;
  const businessCount = items.filter((i) => (i.domain || '').toLowerCase() === 'business').length;
  const personalCount = items.filter((i) => (i.domain || '').toLowerCase() === 'personal').length;

  return (
    <div className="mt-1 flex-1 flex flex-col" data-tutorial-id="sprint-card">
      {/* Header aligned to PartialSalesMiniList */}
      <div className="mb-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="mb-1">{sprint.name || 'Next Sprint'}</h3>
            <h4>90-day actions to close gaps and build value.</h4>
          </div>
          <StatusPill status={statusText} />
        </div>
      </div>

      {/* Progress row */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <div className="text-succession-text-secondary">Progress</div>
          <div className="text-succession-text-primary font-medium">{Math.round(pct * 100)}% complete</div>
        </div>
        <ProgressBar value={pct} />
      </div>

      {/* Tasks list - match listing row spacing */}
      <div className="mt-4 space-y-3">
        {snapshot.length === 0 ? (
          <div className="text-sm text-succession-text-secondary">No upcoming tasks. Review your sprint to pick next steps.</div>
        ) : snapshot.map((t) => {
          const isDone = String(t.status).toLowerCase() === 'completed';
          return (
            <Card className="px-3 py-3">
              <div className="grid grid-cols-12 items-center gap-3">
                {/* Col 1: Icon */}
                <div className="col-span-1 flex items-center justify-center">
                  <div className="w-9 h-9 rounded-lg bg-succession-bg-secondary flex items-center justify-center">
                    {isDone ? (
                      <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></svg>
                    ) : (
                      <svg className="w-5 h-5 text-succession-text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/></svg>
                    )}
                  </div>
                </div>
                {/* Col 2: Title + badges */}
                <div className="col-span-9 min-w-0">
                  <div className="text-sm font-semibold text-succession-text-primary truncate" title={t.title}>{t.title}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <TypeBadge type={t.cepaCategory} />
                    <DomainBadge domain={t.domain} />
                  </div>
                </div>
                {/* Col 3: Link CTA to sprint */}
                <div className="col-span-2 text-right">
                  <Button as="a" href="/succession/sprint" variant="ghost" size="sm">Open</Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Footer breakdown + CTA like PartialSales */}
      <div className="mt-3 pt-2 flex items-center justify-between text-sm text-succession-text-secondary">
        <span className="inline-flex items-center">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21V7a2 2 0 012-2h3v16"/><path d="M9 21V5h3v16"/><path d="M15 21V10h3a2 2 0 012 2v9"/></svg>
          <span className="text-succession-text-primary font-medium mr-1">{businessCount}</span> Business
        </span>
        <span className="w-1 h-1 rounded-full bg-succession-border" />
        <span className="inline-flex items-center">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-3-3.87M4 21v-2a4 4 0 013-3.87M7 7a4 4 0 108 0 4 4 0 10-8 0z"/></svg>
          <span className="text-succession-text-primary font-medium mr-1">{personalCount}</span> Personal
        </span>
      </div>

      <div className="mt-3 pt-2 flex justify-center mt-auto">
        <Button as="a" href="/succession/sprint" variant="ghost" size="sm">View Full Sprint &gt;</Button>
      </div>
    </div>
  );
};

export default MiniSprintDashboard;


